/**
 * PHASE 3 OPTIMIZATION - QuickView Performance Utilities
 * 
 * Performance optimization utilities specifically for QuickView functionality.
 * Handles preloading, caching, and performance monitoring.
 */

import { TIMING, PERFORMANCE_PENALTIES } from '@/constants';
import { productionLogger } from '@/utils/production-logger';

// QuickView performance metrics tracking
interface QuickViewMetrics {
  openTime: number;
  renderTime: number;
  interactionCount: number;
  lastUsed: number;
}

// Cache for QuickView performance data
const quickViewCache = new Map<string, QuickViewMetrics>();

/**
 * Preload QuickView modal when user hovers over product cards
 * Reduces perceived loading time by preloading on interaction hints
 */
export const preloadQuickView = (() => {
  let isPreloaded = false;
  
  return () => {
    if (isPreloaded) return;
    
    // Use dynamic import to preload the module
    import('@/components/QuickViewModal')
      .then(() => {
        isPreloaded = true;
        productionLogger.debug('QuickView modal preloaded successfully');
      })
      .catch((error) => {
        productionLogger.warn('Failed to preload QuickView modal:', error);
      });
  };
})();

/**
 * Track QuickView performance metrics
 */
export const trackQuickViewPerformance = (productId: string, action: 'open' | 'render' | 'interact') => {
  const now = Date.now();
  const existing = quickViewCache.get(productId) || {
    openTime: 0,
    renderTime: 0,
    interactionCount: 0,
    lastUsed: 0
  };
  
  switch (action) {
    case 'open':
      existing.openTime = now;
      break;
    case 'render':
      existing.renderTime = now - existing.openTime;
      break;
    case 'interact':
      existing.interactionCount++;
      break;
  }
  
  existing.lastUsed = now;
  quickViewCache.set(productId, existing);
  
  // Log performance issues
  if (action === 'render' && existing.renderTime > TIMING.ANIMATION_SLOW) {
    productionLogger.warn(`QuickView render time exceeded threshold: ${existing.renderTime}ms for product ${productId}`);
  }
};

/**
 * Optimize QuickView based on usage patterns
 */
export const optimizeQuickViewExperience = () => {
  if (typeof window === 'undefined') return;
  
  // Clear old cache entries to prevent memory leaks
  const cutoffTime = Date.now() - (30 * 60 * 1000); // 30 minutes
  for (const [productId, metrics] of quickViewCache.entries()) {
    if (metrics.lastUsed < cutoffTime) {
      quickViewCache.delete(productId);
    }
  }
  
  // Prefetch images for frequently viewed products
  const frequentlyViewed = Array.from(quickViewCache.entries())
    .filter(([, metrics]) => metrics.interactionCount >= 3)
    .sort(([, a], [, b]) => b.interactionCount - a.interactionCount)
    .slice(0, 5); // Top 5 most interacted products
    
  frequentlyViewed.forEach(([productId]) => {
    productionLogger.debug(`Product ${productId} marked for priority caching (${quickViewCache.get(productId)?.interactionCount} interactions)`);
  });
};

/**
 * Debounced QuickView opener to prevent rapid successive calls
 */
export const createDebouncedQuickViewOpener = (opener: (product: any) => void, delay: number = TIMING.SHORT_DELAY) => {
  let timeoutId: NodeJS.Timeout | null = null;
  
  return (product: any) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      opener(product);
      timeoutId = null;
    }, delay);
  };
};

/**
 * Initialize QuickView optimization on app startup
 */
export const initializeQuickViewOptimizations = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
  // Setup periodic cache cleanup
  setInterval(optimizeQuickViewExperience, 5 * 60 * 1000); // Every 5 minutes
  
  // Preload on first user interaction
  const preloadOnInteraction = () => {
    preloadQuickView();
    // Remove listeners after first preload
    document.removeEventListener('mouseover', preloadOnInteraction);
    document.removeEventListener('touchstart', preloadOnInteraction);
  };
  
  document.addEventListener('mouseover', preloadOnInteraction, { once: true, passive: true });
  document.addEventListener('touchstart', preloadOnInteraction, { once: true, passive: true });
  
  productionLogger.debug('QuickView optimizations initialized');
};

// Export performance constants specific to QuickView
export const QUICKVIEW_PERFORMANCE = {
  PRELOAD_DELAY: TIMING.SHORT_DELAY,
  RENDER_TIMEOUT: TIMING.ANIMATION_SLOW,
  CACHE_DURATION: 30 * 60 * 1000, // 30 minutes
  MAX_CACHED_ITEMS: 50
} as const;